import React, { useContext, useMemo, useState } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { Button, Stack } from "@mui/material";
import {
  DataGridActionsColumn,
  StatusDropdown,
  TableView1,
  useStatusColumn,
} from "@/components";
import { string, bool, oneOf } from "prop-types";
import { AddPartner } from "./";
import { usePageTitle, usePartnersList, useUpdatePartner } from "@/hooks";
import { getRoleTypeNameFromUser } from "@/utils";
import { Link, useNavigate } from "react-router";
import type { StatusOption } from "@/hooks/common/useStatusArray";

interface PartnersListRow {
  id: string;
  name?: string;
  companyUrl?: string;
  appUrl?: string;
  status?: string;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUser?: string | null;
  smtpPassword?: string | null;
  smtpUseTls?: boolean | null;
  smtpUseSsl?: boolean | null;
  smtpFromEmail?: string | null;
  smtpFromName?: string | null;
  usersCount?: number;
  [key: string]: unknown;
}

interface PartnersListProps {
  parentPath?: string;
  showAdd?: boolean;
  showExport?: boolean;
  showEdit?: boolean;
  showTenants?: boolean;
  showDeleteSingle?: boolean;
  showChangePassword?: boolean;
  showCompanies?: boolean;
  showLoginAs?: boolean;
  showSettings?: boolean;
  partnerFilter: "all" | "system" | "partner" | "company";
}

export const PartnersList = (props: PartnersListProps) => {
  //region Loading data
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  const parentPath = props.parentPath || "system-admin";
  const { user } = useUser();
  const roleType = getRoleTypeNameFromUser(user);

  // Set page title
  usePageTitle(gettext("Partners"));

  const { partnerList, loading } = usePartnersList();

  const { updatePartner, updating } = useUpdatePartner();

  // Use the reusable status column hook
  const { statusColumn, StatusMenu } = useStatusColumn({
    onStatusChange: (row, newStatus) => {
      updatePartner({
        variables: {
          partnerId: row.id,
          status: newStatus,
        },
      });
    },
    updating,
  });

  //region React Hook (useState)
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<PartnersListRow | {}>({});
  const [inputStatus, setInputStatus] = useState<string | StatusOption>("all");
  //endregion React Hook (useState)

  //region React Hook (useMemo)
  const showItems = useMemo(() => {
    return {
      showEdit: props.showEdit ?? false,
      showTenants: props.showTenants ?? false,
      showUsers: true,
      showDeleteSingle: props.showDeleteSingle ?? false,
      showChangePassword: props.showChangePassword ?? false,
      showCompanies: props.showCompanies ?? false,
      showLoginAs: props.showLoginAs ?? false,
      showSettings: props.showSettings ?? true,
    };
  }, [
    props.showEdit,
    props.showTenants,
    props.showDeleteSingle,
    props.showChangePassword,
    props.showCompanies,
    props.showLoginAs,
    props.showSettings,
  ]);

  const showActionsColumn = useMemo(() => {
    const { showAdd, ...filteredShowItems }: any = showItems;
    void showAdd;
    return Object.values(filteredShowItems).some((item) => item === true);
  }, [showItems]);

  const rows = useMemo(() => {
    let returnValue = partnerList;
    const statusValue = (inputStatus as any)?.value || inputStatus || "";

    if (statusValue && statusValue !== "all") {
      returnValue = returnValue.filter((row) => row.status === statusValue);
    }
    return returnValue;
  }, [inputStatus, partnerList]);

  // useEffect(() => {
  //   switch (props.partnerFilter) {
  //     case "all":
  //       setTitle(gettext("All Partners"));
  //       if (!isEqual(usersList, allUsers)) {
  //         setLoading(loadingAllUsers);
  //         setUsersList(allUsers);
  //       }
  //       break;
  //     case "system":
  //       setTitle(gettext("System Partners"));
  //       if (!isEqual(usersList, systemUsers)) {
  //         setLoading(loadingSystemUsers);
  //         setUsersList(systemUsers);
  //       }
  //       break;
  //     case "partner":
  //       setTitle(gettext("Partner Users for ") + partnerName);
  //       if (!isEqual(usersList, partnerUsers)) {
  //         setLoading(loadingPartnerUsers);
  //         setUsersList(partnerUsers);
  //       }
  //       break;
  //     case "company":
  //       setTitle(gettext("Company Users for ") + companyName);
  //       if (!isEqual(usersList, companyUsers)) {
  //         setLoading(loadingCompanyUsers);
  //         setUsersList(companyUsers);
  //       }
  //       break;
  //     default:
  //       setUsersList([]);
  //       break;
  //   }
  // }, [
  //   allUsers,
  //   companyName,
  //   companyUsers,
  //   gettext,
  //   loadingAllUsers,
  //   loadingCompanyUsers,
  //   loadingPartnerUsers,
  //   loadingSystemUsers,
  //   partnerName,
  //   partnerUsers,
  //   props.partnerFilter,
  //   systemUsers,
  //   usersList,
  // ]);

  //endregion React Hook (useMemo)

  /**
   * Handle menu click actions for partners
   * @param action - The action to perform
   * @param row - The partner row data
   */
  const menuClick = (action: string, row: PartnersListRow) => {
    switch (action) {
      case "partner-edit":
        handleEdit(row);
        break;
      case "partner-tenants": {
        const id = row?.id || "invalid-id";
        navigate(`/${parentPath}/partners/${id}/tenants`, {
          state: { parentPath: `${parentPath}/partners` },
        });
        break;
      }
      case "partner-users": {
        const id = row?.id || "invalid-id";
        navigate(`/${parentPath}/partners/${id}/users`);
        break;
      }
      case "partner-settings": {
        const id = row?.id || "invalid-id";
        navigate(`/${parentPath}/partners/${id}/settings`);
        break;
      }
      case "partner-delete":
        // Handle delete action
        break;
      case "partner-password":
        // Handle password change
        break;
      case "partner-company":
        // Handle company action
        break;
      case "partner-login":
        // Handle login as action
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
      flex: 1, // Adjust this flex value as needed
    },
    {
      field: "name",
      headerName: gettext("Name"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    {
      field: "companyUrl",
      headerName: gettext("Website"),
      headerAlign: "left",
      type: "string",
      flex: 1,
      renderCell: (params: { value?: string }) => (
        <a href={params.value} target="_blank" rel="noopener noreferrer">
          {params.value}
        </a>
      ),
    },
    {
      field: "appUrl",
      headerName: gettext("App"),
      headerAlign: "left",
      type: "string",
      flex: 1,
      renderCell: (params: { value?: string }) => (
        <a href={params.value} target="_blank" rel="noopener noreferrer">
          {params.value}
        </a>
      ),
    },
    ...(roleType !== "company" ? [statusColumn] : []),
    {
      field: "usersCount",
      headerName: gettext("Users"),
      maxWidth: 100,
      headerAlign: "center",
      type: "number",
      align: "center",
      renderCell: (params: { row: PartnersListRow; value?: number }) => (
        <Button
          variant="outlined"
          component={Link}
          to={`/${parentPath}/partners/${params.row.id}/users`}
        >
          {params.value}
        </Button>
      ),
    },
    ...DataGridActionsColumn(
      showActionsColumn,
      "partner",
      showItems,
      menuClick,
    ),
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        email: true,
        role: false,
        country: false,
        timezone: false,
        status: true,
        partnerName: false,
      },
    },
  };

  const handleStatusChange = (event: string | StatusOption) => {
    setInputStatus(event as any);
  };

  const postToolbar = () => {
    return (
      <>
        <Stack direction="row" spacing={1} sx={{ pl: 1, alignItems: "center" }}>
          <StatusDropdown
            onChange={handleStatusChange}
            defaultValue={
              typeof inputStatus === "string" ? inputStatus : inputStatus?.value
            }
            showAll
            required
            visualStyle="autoComplete"
          />
          {props.showAdd && (
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                ml: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add Partner")}
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const handleCloseAddForm = () => {
    setAddFormOpen(false);
    setEditRow({});
  };

  const handleEdit = (row: PartnersListRow) => {
    setEditRow(row);
    setAddFormOpen(true);
  };

  return (
    <>
      <TableView1
        title={gettext("Partners")}
        rows={rows}
        // rows={[]}
        columns={columns}
        loading={loading}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        showGridToolbarQuickFilter
        showGridToolbarExport={!!props.showExport}
      />
      <StatusMenu />
      <AddPartner
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />
    </>
  );
};

PartnersList.propTypes = {
  parentPath: string,
  showAdd: bool,
  showExport: bool,
  showEdit: bool,
  showTenants: bool,
  showDeleteSingle: bool,
  showChangePassword: bool,
  showCompanies: bool,
  showLoginAs: bool,
  showSettings: bool,
  partnerFilter: oneOf(["all", "system", "partner", "company"]).isRequired,
};
